// src/api/auth.js
import axios from 'axios';

// ---------------------------------------------------------------------------
// Token storage helpers — cross-origin Bearer fallback
// When cookies are blocked (different domains), the session_token returned
// by the login API is stored in localStorage and attached as
// Authorization: Bearer on every request, alongside X-Tenant-Name.
// ---------------------------------------------------------------------------
export const storeTokenLocally = (sessionToken, tenantName) => {
    if (sessionToken) localStorage.setItem("session_token", sessionToken);
    if (tenantName)   localStorage.setItem("tenant_name_local", tenantName);
};

export const clearTokenLocally = () => {
    localStorage.removeItem("session_token");
    localStorage.removeItem("tenant_name_local");
};

export const addBearerInterceptor = (axiosInstance) => {
    axiosInstance.interceptors.request.use((config) => {
        const token      = localStorage.getItem("session_token");
        const tenantName = localStorage.getItem("tenant_name_local");
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        if (tenantName && !config.headers["X-Tenant-Name"]) {
            config.headers["X-Tenant-Name"] = tenantName;
        }
        return config;
    });

    // When the API rotates the session token it revokes the old one, so pick
    // up the replacement from X-New-Session-Token or the next request 401s.
    const saveRotatedToken = (response) => {
        const newToken = response?.headers?.["x-new-session-token"];
        if (newToken) localStorage.setItem("session_token", newToken);
    };
    axiosInstance.interceptors.response.use(
        (response) => {
            saveRotatedToken(response);
            return response;
        },
        (error) => {
            saveRotatedToken(error?.response);
            return Promise.reject(error);
        }
    );
};

  // const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
  const API_BASE_URL = window.__ENV__?.API_BASE_URL
  //  || 'http://localhost:5000';

  // Want this for replit to pull url from env.js
  // const API_BASE_URL =
  //   window.__ENV__?.API_BASE_URL ?? import.meta?.env?.VITE_API_BASE_URL ?? "";
  
if (!API_BASE_URL) {
    throw new Error("API_BASE_URL is not configured");
}
export const login = async (username, password) => {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ username, password }),
        });

        // If successful, return parsed JSON
        if (response.ok) {
            const data = await response.json();
            // Store token + tenant_name for cross-origin Bearer fallback
            storeTokenLocally(data.session_token, data.tenant_name);
            return data;
        }

        // Handle non-200 responses (like 400/401)
        let errorMessage = 'Login failed. Please check your credentials.';
        try {
            const errorData = await response.json(); // Try to parse error body
            if (errorData?.errorMessage) {
                errorMessage = errorData.errorMessage;
            } else if (errorData?.message) {
                errorMessage = errorData.message;
            } else if (errorData?.error_message) {
                errorMessage = errorData.error_message;
            } else if (errorData?.error_code) {
                errorMessage = errorData.error_code;
            }
        } catch {
            // If parsing fails, fall back to default message
        }

        throw new Error(errorMessage);

    } catch (error) {
        // Network or other unexpected errors
        throw new Error(error.message || 'Could not connect to the API server.');
    }
};

// --- General API Client for context passing ---
export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true, 
    headers: {
        'Content-Type': 'application/json',
        // Added for server compatibility when using credentials/cookies
        'X-Requested-With': 'XMLHttpRequest', 
    },
});

// Attach Bearer token interceptor so validateSession / logout work cross-origin
addBearerInterceptor(apiClient);

/**
 * Validate the current session against the backend.
 *
 * Returns:
 *   true  — session is valid  OR  the check failed transiently (DB error,
 *           network hiccup) — we do NOT log the user out in either case;
 *           a real 401 on any subsequent API call will still trigger logout
 *           via the axios response interceptor in useApiClient.
 *   false — session is definitively invalid (expired, not found, missing
 *           credentials) — the caller should log the user out.
 */
export const validateSession = async () => {
    try {
        const response = await apiClient.get("/auth/validate");
        const data = response.data;

        if (data?.valid === true) return true;

        // Transient server-side error (e.g. Cosmos DB timeout).
        // We cannot confirm the session is invalid — keep the user in.
        if (data?.transient === true) return true;

        return false;
    } catch {
        // Network error, CORS failure, etc.  Treat as transient — keep the user in.
        return true;
    }
};

export const logout = () => {
    // Session is cookie-based — just POST to /auth/logout with credentials included.
    // The backend reads the session_token cookie directly.
    return apiClient.post('/auth/logout');
};


// NOTE: The interceptor logic (for automatically adding tenant/org IDs) 
// is implemented in the useApiClient hook in AuthContext to access the state.
